import React, { useState } from 'react';
import { OperaWindow, OperaSection, OperaField, type SideButton } from './OperaWindow';
import { deriveSection } from './AvailabilityWeekGrid';
import {
  staffAvailabilityApi,
  ApiError,
  type StaffReservation,
  type ReservationAssignment,
  type WeekDeclaration,
  type CallOffReason,
} from '../lib/api';

/**
 * Actions on a single rostered spot.
 *
 * Call-off does not delete the declaration — it moves to CALLED_OFF, which
 * releases the spot so it reopens, while the row and its history
 * survive for reporting. Reason is optional: a manager taking a phone call
 * mid-shift shouldn't be forced to categorise it before recording it.
 */

const REASON_OPTIONS: Array<{ value: '' | CallOffReason; label: string }> = [
  { value: '',                   label: 'No reason given' },
  { value: 'SICK',               label: 'Sick' },
  { value: 'PERSONAL_EMERGENCY', label: 'Personal emergency' },
  { value: 'TRANSPORT_FAILURE',  label: 'Transport failure' },
  { value: 'STAFF_CANCELLED',    label: 'Cancelled by us' },
  { value: 'OTHER',              label: 'Other' },
];

interface Props {
  shift: StaffReservation;
  assignment: ReservationAssignment;
  onClose: () => void;
  onChanged: () => void;
  /** Slot-level editing still needs a way in from a card. */
  onEditReservation: () => void;
}

export function SpotActionsModal({ shift, assignment, onClose, onChanged, onEditReservation }: Props) {
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState<'' | CallOffReason>('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleCallOff() {
    setBusy(true); setError(null);
    try {
      await staffAvailabilityApi.callOff(assignment.id, {
        reason: reason || undefined,
        notes: notes.trim() || undefined,
      });
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  const sideButtons: SideButton[] = confirming
    ? [
        { label: 'Record call-off', onClick: handleCallOff, danger: true, disabled: busy },
        { label: 'Back',            onClick: () => setConfirming(false), disabled: busy },
      ]
    : [
        { label: 'Call off…',  onClick: () => setConfirming(true), danger: true, disabled: busy },
        { label: 'Edit shift…', onClick: onEditReservation, disabled: busy },
        { label: 'Close',       onClick: onClose,     disabled: busy },
      ];

  return (
    <OperaWindow
      title="ROSTERED SPOT"
      status={`${assignment.workingName} · ${shift.date} · ${shift.startTime}-${shift.endTime}`}
      onClose={onClose}
      sideButtons={sideButtons}
    >
      {error && <div className="modal-error">{error}</div>}

      {confirming ? (
        <OperaSection title="Call off">
          <p style={{ margin: '4px 0 10px', fontSize: 12 }}>
            <strong>{assignment.workingName}</strong> comes off this shift and the
            spot reopens. Her declaration is kept, not deleted — the call-off is
            recorded against her for reporting, and can be reversed.
          </p>
          <OperaField label="Reason" hint="optional">
            <select
              value={reason}
              onChange={e => setReason(e.target.value as '' | CallOffReason)}
              autoFocus
            >
              {REASON_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </OperaField>
          <OperaField label="Notes">
            <input
              type="text"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="optional"
              maxLength={200}
            />
          </OperaField>
        </OperaSection>
      ) : (
        <OperaSection title="Rostered">
          <p style={{ margin: '4px 0 10px', fontSize: 12 }}>
            <strong>{assignment.workingName}</strong> is rostered on this shift
            {assignment.status === 'PENDING_OVERFLOW' && ' (awaiting a spot)'}.
          </p>
        </OperaSection>
      )}
    </OperaWindow>
  );
}

// ---------------------------------------------------------------------------

interface DeclarationProps {
  declaration: WeekDeclaration;
  onClose: () => void;
  onChanged: () => void;
}

/**
 * Actions on a declaration that isn't a rostered spot — custom hours she set
 * herself, or any declaration that has already been called off.
 *
 * Confirmed: call off, the same flow and the same AvailabilityEvent trail as a
 * rostered spot. Called off: reinstate, which refuses with SHIFT_FULL if the
 * spot was refilled meanwhile (custom hours have no slot, so that can't happen
 * to them).
 *
 * The declared hours are deliberately read-only. She proposed them and a
 * manager approved them as proposed; a manager approving *different* hours is
 * a different feature, not an edit box hidden in here.
 */
export function DeclarationActionsModal({ declaration, onClose, onChanged }: DeclarationProps) {
  const calledOff = declaration.status === 'CALLED_OFF';

  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState<'' | CallOffReason>('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blocked, setBlocked] = useState(false);

  async function handleCallOff() {
    setBusy(true); setError(null);
    try {
      await staffAvailabilityApi.callOff(declaration.id, {
        reason: reason || undefined,
        notes: notes.trim() || undefined,
      });
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  async function handleReinstate() {
    setBusy(true); setError(null);
    try {
      await staffAvailabilityApi.reinstate(declaration.id);
      onChanged();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'SHIFT_FULL') setBlocked(true);
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  const hours = declaration.customStart && declaration.customEnd
    ? `${declaration.customStart} – ${declaration.customEnd}`
    : declaration.shiftStart && declaration.shiftEnd
      ? `${declaration.shiftStart} – ${declaration.shiftEnd}`
      : null;

  let sideButtons: SideButton[];
  if (calledOff) {
    sideButtons = [
      { label: 'Reinstate', onClick: handleReinstate, primary: true, disabled: busy || blocked },
      { label: 'Close',     onClick: onClose,         disabled: busy },
    ];
  } else if (confirming) {
    sideButtons = [
      { label: 'Record call-off', onClick: handleCallOff, danger: true, disabled: busy },
      { label: 'Back',            onClick: () => setConfirming(false), disabled: busy },
    ];
  } else {
    sideButtons = [
      { label: 'Call off…', onClick: () => setConfirming(true), danger: true, disabled: busy },
      { label: 'Close',     onClick: onClose, disabled: busy },
    ];
  }

  const modeLabel = declaration.mode === 'CUSTOM' ? 'CUSTOM HOURS' : 'DECLARATION';

  return (
    <OperaWindow
      title={calledOff ? 'REVERSE CALL-OFF' : modeLabel}
      status={`${declaration.workingName} · ${declaration.date}${hours ? ` · ${hours}` : ''}`}
      onClose={onClose}
      sideButtons={sideButtons}
    >
      {error && <div className="modal-error">{error}</div>}

      <OperaSection title="Declaration">
        <OperaField label="Service Provider">
          <span className="opera-readonly-strong">{declaration.workingName}</span>
        </OperaField>
        <OperaField label="Date">
          <span>{declaration.date}</span>
        </OperaField>
        {hours && (
          <OperaField label="Declared hours" hint="Set by her — not editable here">
            <span>{hours}</span>
          </OperaField>
        )}
        {declaration.mode === 'CUSTOM' && (
          <OperaField
            label="Section"
            hint={declaration.section ? undefined : 'derived from the hours — never set at approval'}
          >
            <span>{sectionLabel(declaration)}</span>
          </OperaField>
        )}
        <OperaField label="Approved by">
          <span>
            {declaration.approvedBy ?? '—'}
            {declaration.approvedAt ? ` · ${declaration.approvedAt.slice(0, 10)}` : ''}
          </span>
        </OperaField>
      </OperaSection>

      {calledOff && (
        <OperaSection title="Reinstate">
          <p style={{ margin: '4px 0 10px', fontSize: 12 }}>
            Puts <strong>{declaration.workingName}</strong> back to confirmed.
            Use this when the call-off was recorded in error — the call-off
            itself stays in her history either way.
          </p>
          {(declaration.cancelReason || declaration.cancelNotes) && (
            <p style={{ margin: '0 0 10px', fontSize: 12, color: 'var(--text-secondary)' }}>
              Recorded reason: {declaration.cancelReason
                ? declaration.cancelReason.toLowerCase().replace(/_/g, ' ')
                : 'none given'}
              {declaration.cancelNotes ? ` — ${declaration.cancelNotes}` : ''}
            </p>
          )}
        </OperaSection>
      )}

      {!calledOff && confirming && (
        <OperaSection title="Call off">
          <p style={{ margin: '4px 0 10px', fontSize: 12 }}>
            <strong>{declaration.workingName}</strong> comes off this day. Her
            declaration is kept, not deleted — the call-off is recorded against
            her for reporting, and can be reversed.
          </p>
          <OperaField label="Reason" hint="optional">
            <select
              value={reason}
              onChange={e => setReason(e.target.value as '' | CallOffReason)}
              autoFocus
            >
              {REASON_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </OperaField>
          <OperaField label="Notes">
            <input
              type="text"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="optional"
              maxLength={200}
            />
          </OperaField>
        </OperaSection>
      )}
    </OperaWindow>
  );
}

/** Stored section, or the fallback the grid renders it in when none was set. */
function sectionLabel(d: WeekDeclaration): string {
  const effective = d.section ?? deriveSection(d.customStart, d.customEnd);
  const pretty = effective.charAt(0) + effective.slice(1).toLowerCase();
  return d.section ? pretty : `${pretty} (derived)`;
}
